import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import PageSEO from "@/components/PageSEO";
import { breadcrumb } from "@/lib/schemas";
import ScheduleNav from "@/components/ScheduleNav";
import VIPUpgradeModal from "@/components/VIPUpgradeModal";
import { Button } from "@/components/ui/button";
import { Rocket, Lightbulb, Code2, Layout, ArrowRight, ExternalLink, ArrowLeft, Star } from "lucide-react";

const VIPDay4 = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const walkAwayWith = [
    { text: "A clear product idea mapped to your niche", icon: Lightbulb },
    { text: "A working prototype built in real time", icon: Code2 },
    { text: "A simple landing page or client-facing portal", icon: Layout },
    { text: "A next-step plan to keep building after the summit", icon: ArrowRight },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#fffcf2] font-sans">
      <PageSEO
        title="VIP Day 4 · Build Your Product | Tech Founder Summit"
        description="VIP Day 4 of the Tech Founder Summit. Map a product idea, build a working prototype, and ship a landing page or client portal in real time."
        canonical="https://marquettabryant.com/vip-day-4"
        jsonLd={[breadcrumb([{name:"Home",path:"/"},{name:"Tech Founder Summit",path:"/tech-founder-summit"},{name:"VIP Day 4",path:"/vip-day-4"}])]}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#2e4053] to-[#1a1a2e] pt-8 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#d8239b]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-[#ff69b4]/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Back Link */}
          <a 
            href="https://marquettabryant.com/tech-founder-summit" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            ← Back to Summit Home
          </a>
          
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#d8239b]/30 to-[#ff69b4]/30 rounded-full mb-6">
              <Star className="w-5 h-5 text-[#ff69b4]" />
              <span className="text-[#ff69b4] font-semibold text-sm">VIP DAY 4 • FEB 17</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Build Your First Digital Product Prototype
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-10 max-w-2xl mx-auto">
              Leave with a working prototype you can keep building after the summit.
            </p>
            
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-10 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              onClick={() => setModalOpen(true)}
            >
              Upgrade to VIP
            </Button>
          </div>
        </div>
      </section>
      
      <ScheduleNav />
      
      <main className="pt-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Outcome Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8 relative overflow-hidden">
              {/* VIP Glow Effect */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#d8239b]/20 to-[#ff69b4]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              
              <div className="relative">
                {/* Outcome Badge */}
                <div className="bg-gradient-to-r from-[#d8239b]/10 to-[#ff69b4]/10 rounded-2xl p-6 mb-6 border border-[#d8239b]/20">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-[#d8239b] text-white text-xs font-bold rounded-full">OUTCOME</span>
                  </div>
                  <p className="text-lg md:text-xl text-[#2e4053] font-medium">
                    Turn your real estate expertise into a digital tool or product you own.
                  </p>
                </div>
              </div>
            </div>

            {/* What This Day Is Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8">
              <h2 className="text-2xl font-bold text-[#2e4053] mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-[#d8239b] to-[#ff69b4] rounded-full"></span>
                What This Day Is
              </h2>
              <div className="space-y-4 mb-8">
                <p className="text-[#2e4053]/80 leading-relaxed text-lg">
                  This is for agents ready to build something beyond transactions.
                </p>
                <p className="text-[#2e4053]/80 leading-relaxed text-lg">
                  You do not need to be a developer.
                  <span className="font-semibold text-[#d8239b]"> You need a problem worth solving and a prototype you can launch.</span>
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-bold text-[#2e4053] mb-4">What We Build Live</h3>
                <p className="text-[#2e4053]/80 leading-relaxed mb-4">
                  Hands-on build day where you will turn an idea into a working digital product concept.
                </p>
                <div className="bg-[#fffcf2] rounded-xl p-4 inline-flex items-center gap-3 border border-gray-100">
                  <span className="text-[#2e4053]/70 text-sm">Real example:</span>
                  <a 
                    href="https://app.ownyourcredit.org/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#1e90ff] hover:text-[#d8239b] font-semibold transition-colors"
                  >
                    app.ownyourcredit.org
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* Walk Away With Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-10">
              <h3 className="text-xl font-bold text-[#2e4053] mb-6">You Will Walk Away With</h3>
              <div className="space-y-4">
                {walkAwayWith.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-5 bg-gradient-to-r from-[#d8239b]/5 to-[#ff69b4]/5 rounded-xl border border-[#d8239b]/10 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#d8239b] to-[#ff69b4] flex items-center justify-center flex-shrink-0 shadow-md">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-[#2e4053] font-medium text-lg">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mb-10">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-12 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                onClick={() => setModalOpen(true)}
              >
                Upgrade to VIP
              </Button>
            </div>

            {/* Bottom Back Link */}
            <div className="text-center">
              <a 
                href="https://marquettabryant.com/tech-founder-summit" 
                className="inline-flex items-center gap-2 text-[#2e4053]/70 hover:text-[#d8239b] transition-colors font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                ← Back to Summit Home
              </a>
            </div>
          </div>
        </div>
      </main>

      <VIPUpgradeModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
};

export default VIPDay4;
